import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  Clock3,
  Compass,
  MapPin,
  Phone,
  Search,
  Sparkles,
} from "lucide-react";
import Seo from "../components/Seo";
import { WhatsAppButton, CallButton, DirectionsButton } from "../components/CTAButtons";
import OfferBanner from "../components/OfferBanner";
import ReviewsSection from "../components/ReviewsSection";
import {
  AREAS,
  BRAND,
  GUIDES,
  HOME_FAQ,
  SERVICES,
  SIGNATURE_PACKAGE,
  TOPIC_PAGES,
  ZORBA,
} from "../data/content";
import {
  breadcrumbSchema,
  daySpaSchema,
  faqSchema,
  multiGraph,
  organizationSchema,
  websiteSchema,
} from "../components/Schema";

const heroImage = "/images/zorba/interior-cover.jpg";

export default function Home({ onBook }) {
  const [area, setArea] = useState("");
  const [service, setService] = useState("");
  const featuredServices = SERVICES.slice(0, 4);
  const featuredAreas = AREAS.slice(0, 6);

  return (
    <>
      <Seo
        title={`${BRAND.name} | Massage & Wellness Spa in Bhubaneswar`}
        description="Discover massage and wellness spa options in Bhubaneswar. Explore services, local areas and book a premium spa experience with our featured partner."
        path="/"
        jsonLd={multiGraph(organizationSchema(), websiteSchema(), daySpaSchema(), faqSchema(HOME_FAQ))}
      />
      <section
        className="hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(15,23,42,.82) 0%, rgba(15,23,42,.55) 48%, rgba(15,23,42,.18) 100%), url(${heroImage})`,
        }}
      >
        <div className="hero-inner">
          <p className="hero-label"><span className="pulse-dot" /> A calmer way to discover Bhubaneswar</p>
          <h1>Find your space<br /><em>to feel restored.</em></h1>
          <p className="hero-copy">
            Explore considered massage and wellness experiences across Bhubaneswar, with clear information
            and a simple way to enquire — no manufactured ratings, no pressure.
          </p>
          <div className="hero-actions">
            <button className="btn btn-light" onClick={() => onBook("General spa enquiry")} data-testid="hero-book-button">
              <CalendarDays size={18} />Book an enquiry
            </button>
            <WhatsAppButton className="btn btn-outline-light" label="WhatsApp now" source="hero" testid="hero-whatsapp-cta" />
          </div>
          <div className="hero-note"><Check size={15} /> {ZORBA.name} is our featured wellness partner</div>
        </div>
      </section>

      <OfferBanner variant="strip" onBook={() => onBook("Monsoon Signature Ritual")} />

      <section className="discovery section-pad" id="discover">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Start with what you need</p>
            <h2>Find a spa experience<br />that fits your day.</h2>
          </div>
          <p className="muted heading-note">
            Filter by what matters. We don't claim real-time availability — enquiries are confirmed with the spa directly.
          </p>
        </div>
        <div className="search-panel">
          <div className="field">
            <MapPin size={18} />
            <label>
              Area
              <select value={area} onChange={(e) => setArea(e.target.value)} data-testid="discovery-area-select">
                <option value="">Anywhere in Bhubaneswar</option>
                {AREAS.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
              </select>
            </label>
          </div>
          <div className="field">
            <Sparkles size={18} />
            <label>
              Experience
              <select value={service} onChange={(e) => setService(e.target.value)} data-testid="discovery-service-select">
                <option value="">Any service</option>
                {SERVICES.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
            </label>
          </div>
          <Link
            to={service ? `/services/${service}/` : area ? `/areas/${area}/` : "/spa-services-bhubaneswar/"}
            className="btn btn-primary"
            data-testid="discovery-search-button"
          >
            <Search size={17} /> Explore options
          </Link>
        </div>
      </section>

      <section className="partner-band section-pad">
        <div className="partner-image">
          <img
            src={ZORBA.heroImage}
            alt={`Reception at ${ZORBA.name}, ${ZORBA.area}, ${ZORBA.city}`}
            loading="lazy"
          />
        </div>
        <div className="partner-content">
          <p className="eyebrow"><span className="status-dot" /> {ZORBA.disclosure}</p>
          <h2>{ZORBA.name}</h2>
          <p className="large-copy">
            <em>"{ZORBA.tagline}."</em> A luxury wellness destination in {ZORBA.area}, {ZORBA.city} —
            with a published menu spanning Swedish, deep tissue, Thai, aroma, Balinese, candle, hot stone,
            jacuzzi and the signature 6-in-1 wellness experience.
          </p>
          <div className="fact-list">
            <div><MapPin size={18} /><span><strong>Address</strong><br />{ZORBA.address}</span></div>
            <div><Phone size={18} /><span><strong>Phone / WhatsApp</strong><br />{ZORBA.phoneDisplay}</span></div>
            <div><Clock3 size={18} /><span><strong>Price range</strong><br />₹2,500 — ₹12,500</span></div>
          </div>
          <div className="actions-row">
            <button className="btn btn-primary" onClick={() => onBook(`Enquiry about ${ZORBA.name}`)} data-testid="partner-book-button">
              Enquire about Zorba <ArrowRight size={17} />
            </button>
            <CallButton source="home_partner_band" testid="partner-call-cta" />
            <Link className="btn btn-secondary" to="/zorba-international-spa/" data-testid="partner-profile-link">
              View partner profile
            </Link>
          </div>
        </div>
      </section>

      <section className="signature-band section-pad">
        <div className="signature-image">
          <img src={SIGNATURE_PACKAGE.image} alt="Zorba signature therapy — candle-lit head massage" loading="lazy" />
        </div>
        <div className="signature-content">
          <p className="eyebrow"><Sparkles size={14} /> {SIGNATURE_PACKAGE.kicker}</p>
          <h2>{SIGNATURE_PACKAGE.name}</h2>
          <p className="price-tag" data-testid="signature-price">90 min · {SIGNATURE_PACKAGE.priceDisplay}</p>
          <p className="large-copy">{SIGNATURE_PACKAGE.intro}</p>
          <ul className="check-list two">
            {SIGNATURE_PACKAGE.inclusions.map((x) => <li key={x}><Check size={16} />{x}</li>)}
          </ul>
          <div className="actions-row">
            <Link to={`/services/${SIGNATURE_PACKAGE.slug}/`} className="btn btn-primary" data-testid="signature-detail-link">
              View signature details <ArrowRight size={17} />
            </Link>
            <WhatsAppButton
              className="btn btn-secondary"
              label="Enquire on WhatsApp"
              message={`Hello Zorba, I'd like to enquire about the Signature Therapy (90 min, ₹6,000).`}
              source="home_signature"
              testid="signature-whatsapp-cta"
            />
          </div>
        </div>
      </section>

      <section className="section-pad service-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Explore by experience</p>
            <h2>Wellness, without<br />the guesswork.</h2>
          </div>
          <Link to="/spa-services-bhubaneswar/" className="text-link" data-testid="all-services-link">
            View all services <ArrowRight size={16} />
          </Link>
        </div>
        <div className="service-grid">
          {featuredServices.map((s, idx) => (
            <Link to={`/services/${s.slug}/`} className="service-card" key={s.slug} data-testid={`service-card-${idx}`}>
              <div className="service-photo" style={{ backgroundImage: `url(${s.image})` }} />
              <div className="service-card-copy">
                <span className="card-kicker">{s.kicker}</span>
                <h3>{s.name}</h3>
                <p>{s.short}</p>
                <ArrowRight size={18} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="areas-band section-pad">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Local knowledge</p>
            <h2>Begin with your<br />part of the city.</h2>
          </div>
          <Link to="/areas/" className="text-link" data-testid="all-areas-link">
            Browse area guides <ArrowRight size={16} />
          </Link>
        </div>
        <div className="area-grid">
          {featuredAreas.map((a, idx) => (
            <Link key={a.slug} to={`/areas/${a.slug}/`} className="area-link" data-testid={`area-link-${idx}`}>
              <Compass size={17} />
              <span>{a.name}</span>
              <ArrowRight size={16} />
            </Link>
          ))}
        </div>
      </section>

      <section className="price-strip section-pad">
        <div>
          <p className="eyebrow">Transparent price list</p>
          <h2>Published prices, no guesswork.</h2>
          <p className="large-copy">
            Our featured partner Zorba International Spa publishes every service price — from ₹2,500 for a 60-minute
            Thai massage to ₹12,500 for a 120-minute couple's experience. The amount you see on this website is
            the amount you pay at the spa.
          </p>
          <div className="actions-row">
            <Link className="btn btn-secondary" to="/spa-prices-bhubaneswar/" data-testid="prices-link">Full price guide</Link>
            <Link className="btn btn-secondary" to="/spa-packages-bhubaneswar/" data-testid="packages-link">Packages</Link>
            <WhatsAppButton label="Ask on WhatsApp" source="price_strip" testid="price-whatsapp-cta" />
          </div>
        </div>
      </section>

      <section className="guides-section section-pad">
        <div className="guides-image">
          <img
            src="https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2?auto=format&fit=crop&w=1200&q=82"
            alt="Aromatherapy oils and a calm spa setting"
            loading="lazy"
          />
        </div>
        <div className="guides-copy">
          <p className="eyebrow">Before you book</p>
          <h2>A little clarity<br />goes a long way.</h2>
          <p className="large-copy">
            Learn how to compare services, ask the right questions and arrive feeling comfortable — whether this
            is your first visit or part of your routine.
          </p>
          <div className="guide-list">
            {GUIDES.slice(0, 4).map((g, idx) => (
              <Link to="/guides/" key={g.slug} data-testid={`guide-link-${idx}`}>
                <span>{g.title}</span>
                <ArrowRight size={17} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="faq-section section-pad">
        <div>
          <p className="eyebrow">Clear answers</p>
          <h2>Good to know.</h2>
          <p className="muted">Everything we publish is kept transparent. No invented reviews, no inflated numbers.</p>
        </div>
        <div className="faq-list">
          {HOME_FAQ.map((faq, i) => (
            <details key={faq.q} open={i === 0}>
              <summary data-testid={`faq-${i}`}>
                {faq.q} <ChevronDown size={18} />
              </summary>
              <p>{faq.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="topics-strip section-pad">
        <p className="eyebrow">Keep exploring</p>
        <h2>Reading paths across the city.</h2>
        <div className="topics-grid">
          {Object.entries(TOPIC_PAGES).slice(0, 8).map(([slug, t], i) => (
            <Link key={slug} to={`/${slug}/`} className="topic-chip" data-testid={`topic-chip-${i}`}>
              {t.title} <ArrowRight size={14} />
            </Link>
          ))}
        </div>
      </section>

      <ReviewsSection />
    </>
  );
}

export const homeBreadcrumbs = [{ label: "Home" }];
export const homeBreadcrumbJsonLd = breadcrumbSchema(homeBreadcrumbs);
